/**
 * Detail sheet for a Police Impound row.
 * Slides in from the right when a dispatcher clicks an impound entry.
 * Shows every captured field plus the live days-remaining countdown, then
 * exposes the same Release & Pay / To NTS / Archive actions that exist on
 * the table row (role-gated identically) so the dispatcher doesn't need
 * to close the sheet to act on the record.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { KeyRound, AlertTriangle, Archive, Phone, MapPin, Calendar, Clock, FileText, Shield, Receipt, Pencil, Mail, Camera, Banknote, ArrowRightLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { fetchImpoundConfig } from "../lib/api";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import ImpoundPhotos from "./ImpoundPhotos";
import PrintReleaseFormButton from "./PrintReleaseFormButton";
import { hasCap, can } from "../lib/roles";
import { overstayDays, daysUntilDue, lastFreeDayIso } from "../lib/tenantTime";
import { useAuth } from "../lib/auth";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso, opts = { dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { ...opts, timeZone: tenantTz() }) : "—";
const fmtDateOnly = (iso) =>
  iso ? new Date(iso).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtTimeOnly = (iso) =>
  iso ? new Date(iso).toLocaleTimeString(tenantLocale(), { timeZone: tenantTz(), hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) : "—";
const PM_LABEL = { cash: "Cash", eftpos: "EFTPOS", credit_card: "Credit Card", bank_transfer: "Bank Transfer", other: "Other" };

/** Returns { days, kind, label } — kind is 'safe'|'soon'|'due'|'overdue'|'closed'. */
export function computeReleaseCountdown(job) {
  if (job.released_at) return { days: null, kind: "closed", label: "Released to owner" };
  if (job.released_to_nts_at) return { days: null, kind: "closed", label: "Signed Over" };
  if (!job.impound_release_due_at) return { days: null, kind: "safe", label: "—" };
  // `impound_release_due_at` is the local midnight that STARTS the first
  // overdue day, so the last free day is the date before it.
  const overdue = overstayDays(job.impound_release_due_at);
  if (overdue > 0) {
    return { days: overdue, kind: "overdue", label: `${overdue} day${overdue === 1 ? "" : "s"} overdue` };
  }
  const remaining = daysUntilDue(job.impound_release_due_at) - 1;  // whole free days left after today
  if (remaining <= 0) return { days: 0, kind: "due", label: "Due today" };
  if (remaining <= 3) return { days: remaining, kind: "soon", label: `${remaining} day${remaining === 1 ? "" : "s"} remaining` };
  return { days: remaining, kind: "safe", label: `${remaining} days remaining` };
}

const KIND_CLS = {
  safe:     "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  soon:     "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  due:      "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
  overdue:  "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  closed:   "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
};

export function CountdownPill({ job, compact = false }) {
  const c = computeReleaseCountdown(job);
  if (c.kind === "closed") return null;
  return (
    <span className={cn(
      "inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded",
      compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1",
      KIND_CLS[c.kind],
    )} data-testid={`impound-countdown-${c.kind}`}>
      {c.kind === "overdue" && <AlertTriangle className="h-3 w-3" />}
      {c.label}
    </span>
  );
}

function Section({ title, icon: Icon, children, testid, aliasTestid }) {
  return (
    <div
      className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/50 dark:bg-slate-800/30"
      data-testid={testid}
      data-testid-alias={aliasTestid}
    >
      {aliasTestid && <span data-testid={aliasTestid} className="sr-only" />}
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
        {Icon && <Icon className="h-3 w-3" />}
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</div>
      <div className={cn("text-slate-900 dark:text-slate-100 break-words", mono && "font-mono")}>{value || "—"}</div>
    </div>
  );
}

export default function ImpoundDetailSheet({
  job, open, onOpenChange, role,
  onRelease, onReleaseToNts, onArchive, onEdit, onLetter,
  onPartPay, onEarlyRelease, onEmailProforma, onMergeDuplicate,
  hasDuplicate = false,
}) {
  const { user: signedInUser } = useAuth();
  // Load configured yards so we can show the dispatcher-friendly NAME
  // ("Parkers Road", "Blackbyre") instead of the raw geocoded address string
  // stored on the job.
  const [yards, setYards] = useState([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchImpoundConfig()
      .then((cfg) => { if (!cancelled) setYards(cfg?.yards || []); })
      .catch(() => setYards([]));
    return () => { cancelled = true; };
  }, [open]);

  if (!job) return null;
  const countdown = computeReleaseCountdown(job);
  const isClosed = !!(job.released_at || job.released_to_nts_at);
  const isArchived = !!job.archived_at;
  const canCreate = hasCap({ role }, "admin", "dispatcher");
  // Sep 2026 — viewer retired; dispatcher absorbed archive capability.
  // Jun 2026 — and the owner can switch it back off per person.
  const canArchive = hasCap({ role }, "admin", "dispatcher")
    && can(signedInUser, "archive_jobs");
  const canSignOver = canCreate && can(signedInUser, "sign_over_vehicles");
  // Letter is available to the same roles that could raise it from the row.
  const canLetter = hasCap({ role }, "admin", "dispatcher");
  // Early Release is only offered while still INSIDE the impound term: past
  // the due date the daily-rate maths exceeds standard Release & Pay, so
  // operators are forced through that flow instead.
  const due = job.impound_release_due_at ? new Date(job.impound_release_due_at) : null;
  const overstayed = !!(due && Date.now() >= due.getTime());
  const b = job.pricing_breakdown || {};
  const li = b.line_items || {};

  // Map this job's drop-off address back to its configured yard NAME. Match
  // first by exact address, then by lat/lng proximity (~50m) since geocoded
  // addresses sometimes return slightly different strings for the same yard.
  const matchedYard = (() => {
    if (!job.drop_off_address && job.drop_off_lat == null) return null;
    const byAddr = yards.find((y) => y && y.address && y.address === job.drop_off_address);
    if (byAddr) return byAddr;
    if (job.drop_off_lat != null && job.drop_off_lng != null) {
      return yards.find((y) =>
        y && y.lat != null && y.lng != null
        && Math.abs(y.lat - job.drop_off_lat) < 0.001
        && Math.abs(y.lng - job.drop_off_lng) < 0.001,
      ) || null;
    }
    return null;
  })();
  const yardDisplay = matchedYard?.name
    ? (matchedYard.address && matchedYard.address !== matchedYard.name
        ? `${matchedYard.name} — ${matchedYard.address}`
        : matchedYard.name)
    : (job.drop_off_address || null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-white dark:bg-slate-900" data-testid="impound-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-700 dark:text-blue-300 shrink-0" />
            <span className="font-mono">{job.rego || "—"}</span>
            <span className="text-base font-normal text-slate-500 dark:text-slate-400">· {job.vehicle}</span>
            {/* The photos sit well below the fold, behind the sticky action
                bar — without this a dispatcher has no way of knowing they
                exist. Clicking jumps straight to them. */}
            {(job.photos?.length || 0) > 0 && (
              <button
                type="button"
                onClick={() => document
                  .getElementById(`impound-photos-${job.id}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 transition-colors hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/70"
                title="Jump to the photos"
                data-testid="impound-detail-photos-chip"
              >
                <Camera className="h-3 w-3" />
                {job.photos.length} photo{job.photos.length === 1 ? "" : "s"}
              </button>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">Full impound record including driver, vehicle, fees and countdown to release.</SheetDescription>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {isArchived && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">Archived</span>}
            {job.released_at && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Released to owner · {fmtDate(job.released_at)}</span>}
            {job.released_to_nts_at && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Signed Over · {fmtDate(job.released_to_nts_at)}</span>}
            {!isClosed && <CountdownPill job={job} />}
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {/* Police paperwork */}
          <Section title="Police Paperwork" icon={FileText} testid="impound-detail-paperwork">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Impound Notice #" value={job.impound_notice_number || job.police_reference} mono />
              <Field label="Tow Authority #" value={job.tow_authority_number} mono />
            </div>
          </Section>

          {/* Driver */}
          <Section title="Driver" icon={Phone} testid="impound-detail-driver">
            <div className="space-y-2">
              <Field label="Full Name" value={job.customer_name || job.impound_owner_name} />
              <Field label="Full Address" value={job.impound_owner_address} />
              <Field label="Telephone" value={job.customer_phone || job.impound_owner_phone} mono />
            </div>
          </Section>

          {/* Vehicle */}
          <Section title="Vehicle" icon={Shield} testid="impound-detail-vehicle">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Make & Model" value={job.vehicle} />
              <Field label="Registration" value={job.rego} mono />
              <Field label="GVM" value={`${job.gvm_kg}kg · ${job.gvm_kg <= 3500 ? "Light" : "Heavy"}`} />
            </div>
          </Section>

          {/* Impound timing */}
          <Section title="Impound" icon={Calendar} testid="impound-detail-impound">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Offence/Impound" value={fmtDateOnly(job.impounded_at)} />
              <Field label="Time of Seizure" value={fmtTimeOnly(job.impounded_at)} />
              <Field label="Term" value={job.impound_term === "28d" ? "28 days" : job.impound_term === "6mo" ? "6 months" : "—"} />
              <Field label="Release Due" value={job.impound_release_due_at ? `${fmtDateOnly(lastFreeDayIso(job.impound_release_due_at))} (end of day)` : "—"} />
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Time until release
                </div>
                <CountdownPill job={job} />
              </div>
              <div className="col-span-2">
                <Field label="Impound Location" value={job.address} />
              </div>
              <div className="col-span-2">
                <Field label="Drop-off (Yard)" value={yardDisplay} />
              </div>
              {job.route_distance_m != null && (
                <div className="col-span-2">
                  <Field label="Round-trip distance" value={`${(job.route_distance_m / 1000).toFixed(1)} km (yard → pickup → yard)`} />
                </div>
              )}
            </div>
          </Section>

          {/* Pricing breakdown */}
          {b.total != null && (
            <Section title="Quoted Pricing" icon={Receipt} testid="impound-detail-pricing" aliasTestid="impound-fee-breakdown">
              <table className="w-full text-sm">
                <tbody>
                  <PriceRow label="Tow Fee" amount={li.tow_fee} note={b.explanations?.tow_fee} />
                  <PriceRow label="Impound Fee" amount={li.impound_fee} note={b.explanations?.impound_fee} />
                  <PriceRow label="Additional Km" amount={li.additional_km_fee} note={b.explanations?.additional_km_fee} />
                  <PriceRow label={`Overstay (${job.days_overstay ?? 0} day${job.days_overstay === 1 ? "" : "s"})`} amount={li.overstay_fee} note={b.explanations?.overstay_fee} />
                  <tr className="border-t-2 border-slate-300 dark:border-slate-600">
                    <td className="py-1.5 font-black uppercase tracking-wider text-sm">TOTAL</td>
                    <td className="py-1.5 text-right font-mono font-black text-lg" data-testid="impound-detail-total">
                      <span data-testid="impound-fee-total">{fmtMoney(b.total)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Section>
          )}

          {/* Payment (if released to driver) */}
          {job.released_at && (
            <Section title="Payment" icon={Receipt} testid="impound-detail-payment">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Method" value={PM_LABEL[job.payment_method] || job.payment_method} />
                <Field label="Amount Paid" value={fmtMoney(job.payment_amount)} mono />
                <div className="col-span-2"><Field label="Received By" value={`${job.payment_received_by || "—"} · ${fmtDate(job.payment_received_at)}`} /></div>
              </div>
            </Section>
          )}

          {/* NTS write-off info */}
          {job.released_to_nts_at && (
            <Section title={`${salvageShort()} Write-Off`} icon={AlertTriangle} testid="impound-detail-nts">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount Written Off" value={fmtMoney(job.released_to_nts_amount_owed)} mono />
                <Field label="Authorised By" value={job.released_to_nts_by} />
                <div className="col-span-2"><Field label="Date" value={fmtDate(job.released_to_nts_at)} /></div>
              </div>
            </Section>
          )}

          {/* Photos — driver tow shots + PhotoShop yard captures */}
          <ImpoundPhotos job={job} />

          {/* Notes */}
          {(job.notes || job.release_notes) && (
            <Section title="Notes" icon={MapPin} testid="impound-detail-notes">
              {job.notes && <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{job.notes}</div>}
              {job.release_notes && <div className="text-sm text-slate-700 dark:text-slate-300 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 whitespace-pre-wrap"><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Release:</span>{job.release_notes}</div>}
            </Section>
          )}
        </div>

        {/* Every impound action lives here now.
            Oct 2026: the list row carried up to eight of these buttons in a
            stacked grid inside one table cell, which made the impound list
            unreadable. The row already opens this panel, so the actions
            belong here where they fit with full labels and grouping. */}
        {/* `-mb-6 pb-6` deliberately: the sheet has 24px of its own bottom
            padding, and without stretching over it the panel content scrolls
            through the gap UNDER the buttons — the owner's street address was
            appearing below the Close button. */}
        <div className="mt-4 sticky -bottom-6 -mb-6 bg-white dark:bg-slate-900 pt-3 pb-6 border-t border-slate-200 dark:border-slate-700 space-y-2" data-testid="impound-detail-actions">
          {!isClosed && (canCreate || canLetter) && (
            <>
              {/* Label is its own fixed column and the buttons get their own
                  wrapping box, so a button that wraps to a second line lines
                  up under the first button rather than sliding left under
                  the label. */}
              <div className="flex gap-2 items-start">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0 pt-2.5">Money</span>
                <div className="flex gap-2 flex-wrap flex-1">
                {canCreate && (
                  <Button onClick={() => { onRelease?.(job); onOpenChange(false); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="impound-detail-release-btn">
                    <KeyRound className="h-4 w-4.5" /> Release &amp; Pay
                  </Button>
                )}
                {canCreate && !overstayed && (
                  <Button onClick={() => { onEarlyRelease?.(job); onOpenChange(false); }} variant="outline" data-testid="impound-detail-early-release-btn" title="Police authorised early release — prorated storage">
                    <KeyRound className="h-4 w-4.5" /> Early Release
                  </Button>
                )}
                {canCreate && (
                  <Button onClick={() => { onPartPay?.(job); onOpenChange(false); }} variant="outline" data-testid="impound-detail-partpay-btn" title="Record / print part payments">
                    <Banknote className="h-4 w-4.5" /> Part Pay
                  </Button>
                )}
                {canCreate && (
                  <Button onClick={() => { onEmailProforma?.(job); onOpenChange(false); }} variant="outline" data-testid="impound-detail-proforma-btn" title="Email the registered owner a proforma invoice they can pay by online banking">
                    <Mail className="h-4 w-4.5" /> Email Proforma
                  </Button>
                )}
              </div>
              </div>

              <div className="flex gap-2 items-start">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0 pt-2.5">Paperwork</span>
                <div className="flex gap-2 flex-wrap flex-1">
                {canLetter && (
                  <Button onClick={() => { onLetter?.(job); onOpenChange(false); }} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="impound-detail-letter-btn">
                    <FileText className="h-4 w-4.5" /> Owner Letter
                  </Button>
                )}
                {canCreate && (
                  <PrintReleaseFormButton
                    jobId={job.id}
                    action="belongings_only"
                    label="Belongings"
                    testId="impound-detail-belongings-btn"
                  />
                )}
                {canSignOver && (
                  <Button onClick={() => { onReleaseToNts?.(job); onOpenChange(false); }} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="impound-detail-nts-btn" title={`Driver did not collect — transfer to ${salvageShort()} in lieu of payment`}>
                    {/* Label stays neutral: the tenant's own salvage entity
                        name belongs in the tooltip, not baked into a button
                        another company will read. */}
                    <AlertTriangle className="h-4 w-4.5" /> Sign Over
                  </Button>
                )}
              </div>
              </div>

              {canCreate && (
                <div className="flex gap-2 items-start">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0 pt-2.5">Record</span>
                  <div className="flex gap-2 flex-wrap flex-1">
                  <Button onClick={() => { onEdit?.(job); onOpenChange(false); }} variant="outline" data-testid="impound-detail-edit-btn">
                    <Pencil className="h-4 w-4.5" /> Edit
                  </Button>
                  {hasDuplicate && (
                    <Button
                      onClick={() => { onMergeDuplicate?.(job); onOpenChange(false); }}
                      variant="outline"
                      className="text-amber-700 dark:text-amber-400 border-amber-400/60 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                      data-testid="impound-detail-merge-dup-btn"
                      title="Another active impound has the same rego — merge them"
                    >
                      <ArrowRightLeft className="h-4 w-4.5" /> Merge duplicate
                    </Button>
                  )}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex gap-2 flex-wrap items-center">
            {isClosed && !isArchived && canArchive && (
              <Button onClick={() => { onArchive?.(job); onOpenChange(false); }} variant="outline" data-testid="impound-detail-archive-btn">
                <Archive className="h-4 w-4.5" /> Archive
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="ml-auto" data-testid="impound-detail-close">Close</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PriceRow({ label, amount, note }) {
  return (
    <tr className="border-b border-slate-200 dark:border-slate-700/60">
      <td className="py-1.5 text-slate-700 dark:text-slate-300">
        {label}
        {note && <div className="text-[10px] text-slate-400 dark:text-slate-500">{note}</div>}
      </td>
      <td className="py-1.5 text-right font-mono text-slate-900 dark:text-slate-100">{fmtMoney(amount)}</td>
    </tr>
  );
}
